import type { ButtonProps } from '@risk-smart/themed-cloudscape-components/button';
import Button from '@risksmart-app/components/src/button';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { WizardStatus } from '@risksmart-app/domain/src/types/consts';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useUpdateAssessmentActivityWithLinkedItems';
import { handleError } from '@/utils/errorUtils';

import { useNavigateToStep } from './hooks/useNavigateToStep';
import { useWizardStore } from './store/useWizardStore';

type WizardButtonProps = {
  basePath: string;
  onClick: ButtonProps['onClick'];
};

export const WizardButton: FC<WizardButtonProps> = ({ onClick, basePath }) => {
  const { wizardStatus, assessmentId, activityId, currentStep } =
    useWizardStore();
  const { navigateToStep } = useNavigateToStep();
  const { t } = useTranslation('common', { keyPrefix: 'wizard' });
  const { addNotification } = useNotifications();
  const { user } = useRisksmartUser();

  const [toolsContent] = useTools();

  const isWizardInProgress = wizardStatus === WizardStatus.InProgress;

  const { updateAssessmentActivity, loading } =
    useUpdateAssessmentActivityWithLinkedItems();

  const startWizard = async (event: CustomEvent<ButtonProps.ClickDetail>) => {
    if (toolsContent === 'wizard') {
      return;
    }
    if (isWizardInProgress) {
      try {
        await updateAssessmentActivity({
          Id: activityId,
          Status: Assessment_Activity_Status_Enum.Inprogress,
          ActivityType: Assessment_Activity_Type_Enum.Task,
          // COI-683: the risk↔activity relationship is the `RiskId` COLUMN, and
          // migration 1786699284000 projects it into `linked_item`. Sending the
          // risk here as well wrote the pair twice — the PK is `(Source, Target)`
          // and therefore directional, so both rows persisted and the risk's
          // Linked Items tab listed the activity twice. `LinkedItemIds` is
          // non-null at every layer, so the empty array is how we say "no generic
          // links to sync", not an omission.
          LinkedItemIds: [],
          ParentId: assessmentId,
          IsWizardAction: true,
          OwnerUserIds: [user!.userId],
          OwnerGroupIds: [],
        });
        navigateToStep(basePath, currentStep);
      } catch (error) {
        handleError(error);
        addNotification({
          type: 'error',
          content: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    } else if (onClick) {
      onClick(event);
    }
  };

  return (
    <Button
      variant={'primary'}
      loading={loading}
      onClick={startWizard}
      // The label is `${'Continue'|'Start'} ${t('wizardName')}` — and
      // `wizardName` is i18n-driven and per-org taxonomy-overridable, so it is
      // not a stable selector. The testid encodes the STATE rather than being a
      // single constant, because this is one button whose label toggles: an
      // e2e spec asserting the Continue path needs to distinguish the two.
      data-testid={isWizardInProgress ? 'continue-rcsa' : 'start-rcsa'}
    >
      {`${isWizardInProgress ? 'Continue' : 'Start'} ${t('wizardName')}`}
    </Button>
  );
};
